import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ClickAwayListener } from "@mui/material";
import DropDown from "../DropDown/DropDown";
import { FILTER_TYPE } from "@/constant/constants";
import { FilterIcon } from "@/constant/imagePath";
import PropTypes from "prop-types";

const BaseFilter = React.memo(({ filterData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});

  const location = useLocation();
  const navigate = useNavigate();

  // Initialize selected filters from URL params
  const initializeSelectedFilters = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const initialFilters = {};
    filterData.forEach((filter) => {
      if (params.has(filter.id)) {
        initialFilters[filter.id] = params.get(filter.id);
      }
    });
    setSelectedFilters(initialFilters);
  }, [filterData, location.search]);
  useEffect(() => {
    initializeSelectedFilters();
  }, [initializeSelectedFilters]);

  const toggleFilter = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleSelectDropdown = useCallback((id, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [id]: value !== undefined ? value : null,
    }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(selectedFilters).forEach(([key, value]) => {
      console.log(value);
      if (key === "filter[time_range]" && value && typeof value === "object") {
        if (value.start && value.end) {
          params.set(key, `${value.start},${value.end}`);
        }
      } else {
        params.set(key, value);
      }
    });

    navigate({ pathname: location.pathname, search: params.toString() });
    setIsOpen(false);
  }, [navigate, location.pathname, selectedFilters]);

  const renderComponent = useCallback(
    (data) => {
      switch (data.type) {
        case FILTER_TYPE.SELECT:
          return (
            <DropDownFilter
              key={data.id}
              data={data}
              selectedValue={selectedFilters[data.id] || ""}
              onSelect={handleSelectDropdown}
            />
          );
        case FILTER_TYPE.DATE:
          return (
            <DateFilter
              key={data.id}
              data={data}
              selectedValue={selectedFilters[data.id] || {}}
              onSelect={handleSelectDropdown}
            />
          );
        default:
          return (
            <div key={data.id} className="text-sm text-gray-500">
              Unsupported Filter Type
            </div>
          );
      }
    },
    [selectedFilters, handleSelectDropdown]
  );

  return (
    <ClickAwayListener
      onClickAway={() => {
        setIsOpen(false);
        initializeSelectedFilters();
      }}
    >
      <div className="relative">
        <div className="flex items-center gap-2">
          <button
            onClick={toggleFilter}
            className="size-8 rounded-[7px] border-[.3] border-[#595959] flex items-center justify-center bg-green-black-gradient text-white shadow-primary-inset"
          >
            <img src={FilterIcon} alt="FilterIcon" />
          </button>
        </div>

        {isOpen && (
          <div className="absolute top-9 left-0 rounded bg-white z-30 w-96 border border-gray-300 shadow-lg">
            <div className="p-3 flex flex-col gap-2 max-h-[70vh] overflow-x-auto">
              {filterData.map(renderComponent)}
              <div className="mt-2 flex justify-end gap-2">
                <button
                  className="h-8 min-w-[96px] px-[16px] bg-white flex items-center justify-center gap-x-2 font-extralight text-sm hover:underline underline-offset-1 rounded-7 shadow-primary-inset"
                  onClick={() => setSelectedFilters({})}
                >
                  Reset
                </button>
                <button
                  onClick={handleApplyFilters}
                  className="h-8 min-w-[96px] px-[16px] bg-golden-gradient flex items-center justify-center gap-x-2 font-extralight text-sm text-white hover:underline underline-offset-1 rounded-7 shadow-primary-inset"
                >
                  <span>Apply</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
});

BaseFilter.propTypes = {
  filterData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      options: PropTypes.array,
    })
  ).isRequired,
};

export default BaseFilter;

const DropDownFilter = React.memo(({ data, selectedValue, onSelect }) => {
  return (
    <div className="flex gap-2 w-full items-center">
      <div className="w-32 text-sm text-gray-700">{data.label}</div>
      <div className="flex-1">
        <DropDown
          options={data.options}
          setValue={({ value }) => onSelect(data.id, value)}
          value={data.options.find((opt) => opt.value === selectedValue) ?? ""}
          menuPortalTarget={document.body} // Render dropdown outside parent
          isSearchable={true}
          required={false}
        />
      </div>
    </div>
  );
});

const DateFilter = React.memo(({ data, selectedValue, onSelect }) => {
  return (
    <div className="flex gap-2 flex-row w-full items-center">
      <div className="w-32 text-sm text-gray-700">{data.label}</div>
      <div className="flex-1 flex gap-2">
        <input
          type="date"
          className="border rounded p-2 w-3/4"
          value={selectedValue.start}
          onChange={(e) =>
            onSelect(data.id, { ...selectedValue, start: e.target.value })
          }
        />
        <input
          type="date"
          className="border rounded p-2 w-3/4"
          value={selectedValue.end}
          onChange={(e) =>
            onSelect(data.id, { ...selectedValue, end: e.target.value })
          }
        />
      </div>
    </div>
  );
});

DropDownFilter.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
  }).isRequired,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelect: PropTypes.func.isRequired,
};

DateFilter.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  selectedValue: PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string,
  }),
  onSelect: PropTypes.func.isRequired,
};
